import type { PartialRecord } from '@/core/helperTypes'
import {
  isImageAnnotationDataPayload,
  isVideoAnnotationDataPayload,
} from '@/modules/Editor/models/annotation/annotationKindValidator'
import type { VideoRaster } from '@/modules/Editor/models/raster/VideoRaster'
import type { VoxelRaster } from '@/modules/Editor/models/raster/VoxelRaster'
import { assertImageRaster } from '@/modules/Editor/models/raster/assertImageRaster'
import { assertVideoRaster } from '@/modules/Editor/models/raster/assertVideoRaster'
import { assertVoxelRaster } from '@/modules/Editor/models/raster/assertVoxelRaster'
import type { ConsensusRasterRemap } from '@/modules/Editor/models/raster/consensusRasterRemap'
import { createRaster } from '@/modules/Editor/models/raster/createRaster'
import { RasterTypes } from '@/modules/Editor/models/raster/rasterTypes'
import type { View } from '@/modules/Editor/views/view'
import { ViewTypes } from '@/modules/Editor/views/viewTypes'
import type { V2AnnotationPayload } from '@/store/types/StageAnnotationPayload'
import mapValues from 'lodash/mapValues'
import {
  deserializeImageRaster,
  RasterTooLargeError,
} from '@/modules/Editor/models/raster/deserializeImageRaster'
import { deserializeVideoOrVoxelRaster } from '@/modules/Editor/models/raster/deserializeVideoOrVoxelRaster'
import { RasterManagerEvents } from '@/modules/Editor/eventBus'

export const createRasterFromDeserializable = (
  view: View,
  rasterAnnotationPayload: V2AnnotationPayload,
  maskAnnotations: PartialRecord<string, V2AnnotationPayload>,
  consensusRemap?: ConsensusRasterRemap,
): void => {
  // Non-default-group rasters in consensus review reference workflow-group mask
  // ids; their default-group copies are present in `maskAnnotations` and reached
  // via `consensusRemap.sourceIdToDefaultId`.
  if (!consensusRemap && !Object.keys(maskAnnotations).length) {
    // Masks are deleted but raster annotation still currently exists.
    // This is an edge case that can happen whilst the BE is still going through and
    // Seeing whether the raster layer should be deleted, which can happen when you have
    // Many keyframes.
    return
  }

  // Skip only if a raster for this exact backend annotation has already been
  // processed. Multiple rasters per file are allowed for consensus review
  // overlays.
  const existing = view.rasterManager.getRasterByApiAnnotationId(rasterAnnotationPayload.id)
  if (existing) {
    // `resolveRasterMapping` clears the raster layer on every frame change.
    // Default-group rasters get re-registered via `addRasterLayerAnnotation`
    // when masks are added to the annotation manager; non-default consensus
    // overlays have no in-memory masks pointing at them, so we re-register
    // them here. (`onCurrentFrameIndexChanged` on the layer handles
    // invalidating their `cachedCanvas` for the new frame.)
    if ((existing.annotationGroupId ?? null) !== null) {
      view.rasterAnnotationLayer.addRaster(existing.id)
    }
    return
  }

  try {
    const raster = createRaster(view)
    raster.apiAnnotationId = rasterAnnotationPayload.id
    raster.annotationGroupId = rasterAnnotationPayload.annotation_group_id ?? null

    const annotationIdToClassIdMapping = mapValues(maskAnnotations, (annotation) => {
      if (!annotation) {
        throw new Error('Annotation not found')
      }
      return annotation.annotation_class_id
    })

    view.rasterManager.addRaster(raster)

    if (view.type === ViewTypes.IMAGE || view.type === ViewTypes.TILED) {
      const imageDataPayload = rasterAnnotationPayload.data

      if (!isImageAnnotationDataPayload(imageDataPayload)) {
        throw new Error('Is not image data payload')
      }

      deserializeImageRaster(
        assertImageRaster(raster),
        imageDataPayload.raster_layer,
        annotationIdToClassIdMapping,
        consensusRemap,
      )
    } else if (
      view.type === ViewTypes.VIDEO ||
      view.type === ViewTypes.STREAM ||
      view.type === ViewTypes.DICOM ||
      view.type === ViewTypes.LEGACY_DICOM
    ) {
      const videoDataPayload = rasterAnnotationPayload.data

      if (!isVideoAnnotationDataPayload(videoDataPayload)) {
        throw new Error('Is not video data payload')
      }

      let videoOrVoxelRaster: VideoRaster | VoxelRaster

      if (raster.type === RasterTypes.VIDEO) {
        videoOrVoxelRaster = assertVideoRaster(raster)
      } else if (raster.type === RasterTypes.VOXEL) {
        videoOrVoxelRaster = assertVoxelRaster(raster)
      } else {
        throw new Error('unsupported raster type for view type')
      }

      // When recreating video raster from the API payload, we need to make sure we have ID in sync.
      videoOrVoxelRaster.idSyncedWithApi = rasterAnnotationPayload.id

      deserializeVideoOrVoxelRaster(
        videoOrVoxelRaster,
        videoDataPayload.frames,
        annotationIdToClassIdMapping,
        true,
        consensusRemap,
      )
    }

    // Register non-default-group rasters with the layer AFTER deserialization.
    // `deserializeImageRaster` / `deserializeVideoOrVoxelRaster` end with an
    // `invalidate()` call that synchronously fires `rasterLayer.render()`. If
    // the raster is already in `_rasters` at that point, that render runs
    // before any mask annotations have been normalised into the annotation
    // manager, so its colour map is empty — and in the non-WebGL path the
    // empty result gets baked into the raster's `cachedCanvas` and
    // `clearInvalidation()` runs, leaving subsequent renders with that blank
    // cache. Default-group rasters are registered later by
    // `addRasterLayerAnnotation` (when the manager is populated); non-default
    // rasters have no in-memory masks pointing at them so we register them
    // here, post-deserialization, leaving them invalidated for the next
    // animation-frame render to repaint with the populated colour map.
    if (raster.annotationGroupId !== null) {
      view.rasterAnnotationLayer.addRaster(raster.id)
    }
  } catch (e) {
    if (e instanceof RangeError || e instanceof RasterTooLargeError) {
      RasterManagerEvents.rasterError.emit({ viewId: view.id }, { error: e })
      return
    }

    throw e
  }
}
